import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { flashcardLibraryService, type RequestGenerationInput } from '../api/flashcardLibraryService';

/**
 * Starts an AI flashcard generation job and polls it every 2s until terminal.
 * Calls `onComplete` exactly once when the job transitions to Completed or Failed.
 */
export function useFlashcardGenerationJob(onComplete?: () => void) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const completedJobIds = useRef(new Set<string>());

  const start = useMutation({
    mutationFn: (input: RequestGenerationInput) => flashcardLibraryService.requestGeneration(input),
    onSuccess: (result) => {
      setActiveJobId(result.jobId);
    },
  });

  const poll = useQuery({
    queryKey: ['flashcard-generation-job', activeJobId],
    queryFn: () => flashcardLibraryService.getJob(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'Pending' || status === 'Running' ? 2000 : false;
    },
  });

  useEffect(() => {
    if (!activeJobId) return;
    const status = poll.data?.status;
    const isTerminal = status === 'Completed' || status === 'Failed';
    if (isTerminal && !completedJobIds.current.has(activeJobId)) {
      completedJobIds.current.add(activeJobId);
      onComplete?.();
      setActiveJobId(null);
    }
  }, [poll.data?.status, activeJobId, onComplete]);

  return {
    startGeneration: start.mutateAsync,
    activeJob: poll.data ?? null,
    isStarting: start.isPending,
    isPolling: !!activeJobId && (poll.data?.status === 'Pending' || poll.data?.status === 'Running'),
    error: (start.error as Error | null) ?? (poll.error as Error | null),
  };
}
